'use client'

// Modules
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react'
import { XMarkIcon, CheckCircleIcon, TrashIcon } from '@heroicons/react/24/outline'

// UI
import { Button } from 'components/catalyst/button';
import { Heading } from 'components/catalyst/heading';
import { Spinner } from 'components/common/Spinner';
import NotificationsList from './NotificationsList';


const NotificationsDialog = ({
  notifications,
  isFetchingNotifications,
  isUpdatingNotifications,
  handleMarkAsRead,
  handleMarkAllAsRead,
  handleDeleteNotification,
  handleDeleteAllNotifications,

  showNotificationsModal,
  handleCloseNotificationsModal,
}) => {
  const { t } = useTranslation('common');
  const [activeTab, setActiveTab] = useState('all');

  // Filter notifications based on active tab
  const filteredNotifications = useMemo(() => {
    if (!notifications) return [];
    if (activeTab === 'unread') {
      return notifications.filter(n => !n.read);
    }
    return notifications;
  }, [notifications, activeTab]);

  // Count unread notifications
  const unreadCount = useMemo(() => {
    if (!notifications) return 0;
    return notifications.filter(n => !n.read).length;
  }, [notifications]);

  return (
    <Dialog open={showNotificationsModal} onClose={handleCloseNotificationsModal} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity duration-200 ease-in-out data-[closed]:opacity-0"
      />

      <div className="fixed inset-0" />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <DialogPanel
              transition
              className="pointer-events-auto w-screen max-w-md transform transition duration-200 ease-in-out data-[closed]:translate-x-full"
            >
              <div className="flex h-full flex-col bg-white dark:bg-zinc-900 shadow-xl">
                {/* Header */}
                <div className="px-4 sm:px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
                  <div className="flex items-center justify-between">
                    <Heading className="text-lg font-semibold">
                      {t('NOTIFICATION_TITLE')}
                    </Heading>

                    <Button
                      plain
                      onClick={handleCloseNotificationsModal}
                      className="!p-1"
                    >
                      <XMarkIcon aria-hidden="true" className="!size-5" />
                    </Button>
                  </div>

                  {/* Tabs */}
                  <div className="flex gap-1 mt-4 p-1 bg-zinc-100 dark:bg-zinc-800 rounded-lg">
                    {[
                      { id: 'all', label: t('NOTIFICATION_TAB_ALL') },
                      { id: 'unread', label: t('NOTIFICATION_TAB_UNREAD') },
                    ].map((tab) => {
                      const isActive = activeTab === tab.id;
                      const count = tab.id === 'all'
                        ? notifications?.length || 0
                        : unreadCount;

                      return (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`
                            flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium
                            transition-all duration-200
                            ${isActive
                              ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white shadow-sm'
                              : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white'
                            }
                          `}
                        >
                          {tab.label}
                          {count > 0 && (
                            <span className={`
                              px-1.5 py-0.5 rounded-full text-xs font-medium
                              ${isActive
                                ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                                : 'bg-zinc-200 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-400'
                              }
                            `}>
                              {count}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
                  {isFetchingNotifications ? (
                    <div className="flex items-center justify-center w-full h-64">
                      <Spinner size="lg" />
                    </div>
                  ) : (
                    <NotificationsList
                      notifications={filteredNotifications}
                      onMarkAsRead={handleMarkAsRead}
                      onDelete={handleDeleteNotification}
                      handleCloseNotificationsModal={handleCloseNotificationsModal}
                    />
                  )}
                </div>

                {/* Footer */}
                {notifications && notifications.length > 0 && (
                  <div className="px-4 sm:px-6 py-3 border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50">
                    <div className="flex gap-2">
                      <Button
                        color='blue'
                        onClick={handleMarkAllAsRead}
                        disabled={isUpdatingNotifications || unreadCount === 0}
                        className="flex-1 justify-center"
                      >
                        <CheckCircleIcon className="w-4 h-4 mr-2" />
                        {t('NOTIFICATION_MARK_ALL_AS_READ')}
                      </Button>
                      <Button
                        color='red'
                        onClick={handleDeleteAllNotifications}
                        disabled={isUpdatingNotifications}
                        className="flex-1 justify-center"
                      >
                        <TrashIcon className="w-4 h-4 mr-2" />
                        {t('NOTIFICATION_DELETE_ALL')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default NotificationsDialog;
